import { useEffect, useRef } from "react";
import { beep } from "../lib/sound";
import { scrollToId } from "../lib/lenis";

// Leaving the catalog upward takes ~2400px of blocked scrolling (about three swipes) before the page lets go.
export function useExitLock(sectionRef, onStep) {
  // leaving the shop upward takes roughly three full swipes of blocked scrolling before the page lets go
  const swipeRef = useRef({ accum: 0, lastEvent: 0, unlockedUntil: 0, touchY: 0, dir: 0, lastStep: 0 });
  useEffect(() => {
    const THRESHOLD = 2400;
    const rect = () => sectionRef.current?.getBoundingClientRect();
    const near = () => {
      const r = rect();
      return r && r.top > -160 && r.top < 700;
    };
    const attempt = (now, amount) => {
      const s = swipeRef.current;
      s.dir = -1;
      if (now < s.unlockedUntil) return false;
      if (now - s.lastEvent > 8000) s.accum = 0;
      s.lastEvent = now;
      s.accum += Math.min(Math.abs(amount), 160);
      const step = Math.min(3, Math.floor(s.accum / (THRESHOLD / 3)));
      if (step !== s.lastStep) {
        s.lastStep = step;
        onStep?.(step);
        if (step > 0) beep(420 + step * 120, 0.05, 0.02);
      }
      if (s.accum >= THRESHOLD) {
        s.accum = 0;
        s.lastStep = 0;
        s.unlockedUntil = now + 4000;
        return false;
      }
      return true;
    };
    const onWheel = (e) => {
      if (e.deltaY >= 0) {
        swipeRef.current.dir = 1;
        swipeRef.current.accum = 0;
        return;
      }
      if (!near()) return;
      if (attempt(performance.now(), e.deltaY)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    const onTouchStart = (e) => {
      swipeRef.current.touchY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      const y = e.touches[0].clientY;
      const dy = y - swipeRef.current.touchY;
      swipeRef.current.touchY = y;
      if (dy <= 0) {
        if (dy < 0) swipeRef.current.dir = 1;
        return;
      }
      if (!near()) return;
      if (attempt(performance.now(), dy * 3)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    // momentum guard: while locked and heading up, the page may never drift above the catalog
    const onScroll = () => {
      const s = swipeRef.current;
      const r = rect();
      if (!r) return;
      if (r.top < -240) s.accum = 0;
      const now = performance.now();
      if (now < s.unlockedUntil || s.dir !== -1) return;
      if (r.top > 68 && r.top < 700) scrollToId("#platform", true);
    };
    const onUnlock = () => {
      swipeRef.current.unlockedUntil = performance.now() + 3500;
    };
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    window.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    window.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("gumz:unlock", onUnlock);
    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("gumz:unlock", onUnlock);
    };
  }, []);
}
